import {
  ComponentPropsWithoutRef,
  ReactNode,
  memo,
  useEffect,
  useRef,
} from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import { Avatar } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import ReactMarkdown, { uriTransformer } from 'react-markdown'
import type { ReactMarkdownProps } from 'react-markdown/lib/ast-to-react'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { colorPalette } from '../../style/colorPalette'
import { AiAgentChatMessage } from './hooks/useAiAgentConversation'
import { MessageSegment, segmentsToText } from './tokenChunks'
import { WbsItemAttachmentChip } from './WbsItemAttachmentChip'
import { WbsItemLinkChip } from './WbsItemLinkChip'
import { WbsItemStatusLinkChip } from './WbsItemStatusLinkChip'
import { parseWbsItemLinkHref } from './wbsLinkHref'
import { parseWbsItemStatusLinkHref } from './wbsStatusLinkHref'

const HistoryArea = styled('div')({
  flexGrow: 1,
  overflowY: 'auto',
  padding: 16,
  backgroundColor: 'transparent',
})

const MessageContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  alignItems: 'flex-start',
  marginBottom: 8,
}))

// 1 メッセージを構成するブロックを縦に積む列。アシスタント応答はツール呼び出しごとに
// 吹き出しが分割され、ツールのチップは吹き出しの外側の独立したブロックになる。
const MessageBlocks = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
  gap: 6,
  maxWidth: '80%',
  marginLeft: isUser ? 0 : 8,
  marginRight: isUser ? 8 : 0,
}))

const MessageBubble = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser' && prop !== 'isError',
})<{
  isUser: boolean
  isError?: boolean
}>(({ isUser, isError }) => ({
  backgroundColor: isError
    ? colorPalette.pink[1]
    : isUser
      ? colorPalette.skyBlue[1]
      : colorPalette.monotone[1],
  color: isError ? colorPalette.pink[7] : colorPalette.monotone[10],
  borderRadius: 12,
  padding: '8px 12px',
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: 14,
}))

// マークダウンをチャットバブル内で表示するためのスタイル。
// 先頭・末尾要素の余白を詰めてバブルの padding と重複させない。
const MarkdownContent = styled('div')({
  // バブル側の pre-wrap を打ち消す（マークダウンはブロック要素で改行を表現するため）
  whiteSpace: 'normal',
  '& > *:first-of-type': {
    marginTop: 0,
  },
  '& > *:last-child': {
    marginBottom: 0,
  },
  '& p': {
    margin: '0 0 8px 0',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '12px 0 6px 0',
    fontWeight: 600,
    fontSize: 15,
  },
  '& ul, & ol': {
    margin: '0 0 8px 0',
    paddingLeft: 20,
  },
  '& li': {
    marginBottom: 2,
  },
  '& blockquote': {
    margin: '8px 0',
    padding: '0 12px',
    borderLeft: `3px solid ${colorPalette.monotone[3]}`,
    color: colorPalette.monotone[7],
  },
  '& code': {
    padding: '1px 4px',
    fontSize: '85%',
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 3,
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
  },
  '& pre': {
    margin: '8px 0',
    padding: 12,
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 6,
    overflow: 'auto',
    '& code': {
      padding: 0,
      backgroundColor: 'transparent',
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    margin: '8px 0',
    '& th, & td': {
      border: `1px solid ${colorPalette.monotone[3]}`,
      padding: '4px 10px',
    },
    '& th': {
      backgroundColor: colorPalette.monotone[2],
      fontWeight: 600,
    },
  },
  // :not([data-wbs-item-link-chip]) で WbsItemLinkChip を除外する。チップは自前の見た目
  // （白地に枠のバッジ）を持つため、ここの汎用リンク色・下線を継承すると崩れてしまう。
  '& a:not([data-wbs-item-link-chip])': {
    color: colorPalette.skyBlue[7],
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
})

// ユーザーが送信時に添付した画像のサムネイル一覧。
const UserImagesRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 6,
})

const UserImageThumb = styled('img')({
  maxWidth: 160,
  maxHeight: 160,
  borderRadius: 8,
  objectFit: 'cover',
})

// ユーザーが送信時に添付したWBSアイテムの一覧。何を渡したかが見えるよう、送信済みバブルにも
// 常に表示する（画像添付と同じ位置づけ）。
const UserWbsItemAttachmentsRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 6,
})

const AiAvatar = styled(Avatar)({
  backgroundColor: colorPalette.skyBlue[7],
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const LoadingBubble = styled('div')({
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: `2px solid ${colorPalette.monotone[2]}`,
  borderTop: `2px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

// エージェントがツールを実行したことを示すチップ。吹き出しの外側に置かれ、
// 複数回の呼び出しは MessageBlocks の縦積みでそのまま 1 行ずつ並ぶ。
const ToolCallChip = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 10,
  backgroundColor: colorPalette.monotone[2],
  color: colorPalette.monotone[7],
  fontSize: 12,
  '& svg': {
    fontSize: 14,
    // アイコンは 1 行目の文字と高さを揃える（引数付きの文言は複数行になりうる）
    marginTop: 2,
    flexShrink: 0,
  },
})

// 引数付きの文言は長くなりうるため 2 行で打ち切る。全文は title 属性で参照できる。
const ToolCallText = styled('span')({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  wordBreak: 'break-word',
})

// 引数名を表示用のラベルに解決する。未知の引数名はそのまま表示して手掛かりを残す。
const resolveArgLabel = (intl: IntlShape, key: string) => {
  const messageId = `aiAgentChat.toolArg.${key}`
  return intl.messages[messageId] ? intl.formatMessage({ id: messageId }) : key
}

// ツール名と引数を表示用の文言に解決する。未知のツールでも実行された事実は伝える。
const ToolCallSegment = ({
  toolName,
  toolInput,
}: {
  toolName: string
  toolInput: Record<string, string>
}) => {
  const intl = useIntl()
  const messageId = `aiAgentChat.tool.${toolName}`
  const label = intl.messages[messageId]
    ? intl.formatMessage({ id: messageId })
    : intl.formatMessage({ id: 'aiAgentChat.tool.unknown' }, { toolName })
  // 引数はエージェントが指定した順序で届く（先頭が必須引数=操作対象になりやすい）ため、そのまま並べる
  const args = Object.entries(toolInput)
    .map(([key, value]) => `${resolveArgLabel(intl, key)}: ${value}`)
    .join(', ')
  const text = args
    ? intl.formatMessage({ id: 'aiAgentChat.tool.withArgs' }, { label, args })
    : label
  return (
    <ToolCallChip title={text}>
      <SettingsOutlinedIcon />
      <ToolCallText>{text}</ToolCallText>
    </ToolCallChip>
  )
}

// react-markdown の既定の transformLinkUri は http/https/mailto/tel 等以外のスキームを
// javascript:void(0) に潰してしまう（unsafe な URL を a コンポーネントに渡さないための挙動）。
// wbs: / status: はこの許可リストに無いため、有効な独自スキームのリンクだけ変換をスキップして
// components.a まで元の値を届ける。それ以外は既定のサニタイズに委ねる。
const transformLinkUri = (uri: string) =>
  parseWbsItemLinkHref(uri) || parseWbsItemStatusLinkHref(uri)
    ? uri
    : uriTransformer(uri)

// リンクテキストは通常 remark が渡す文字列 1 要素だが、念のため配列（太字等が混ざる場合）も
// 素朴に連結して拾う。書式は落ちるが、チップの表示名としては文字が取れれば十分。
const linkChildrenToText = (children: ReactNode): string => {
  if (typeof children === 'string') {
    return children
  }
  if (Array.isArray(children)) {
    return children
      .map(child => (typeof child === 'string' ? child : ''))
      .join('')
  }
  return ''
}

// 応答本文の `[名称](wbs:コード)` / `[Done](status:DONE)` をチップに差し替える。
//
// linkTarget="_blank" は react-markdown v8 では既定の a レンダラにしか効かず、components.a を
// 渡すと無視される仕様のため、wbs: 以外の通常リンクはここで target/rel を明示して既存の
// 「新しいタブで開く」挙動を維持する（忘れると外部リンクが同一タブで開く回帰になる）。
const MarkdownLink = ({
  href,
  children,
  // react-markdown が付与する node/sourcePosition/index/siblingCount は DOM の a 要素には
  // 存在しない属性のため、素の <a> へそのまま渡さないよう分割代入で取り除く。
  node,
  sourcePosition,
  index,
  siblingCount,
  ...rest
}: ComponentPropsWithoutRef<'a'> & ReactMarkdownProps) => {
  const wbsItemCode = href ? parseWbsItemLinkHref(href) : null
  if (wbsItemCode) {
    // リンクテキストが空（`[](wbs:コード)` 等、想定外の入力）のときはコードを表示名の代用にする。
    const label = linkChildrenToText(children).trim() || wbsItemCode
    return <WbsItemLinkChip code={wbsItemCode} label={label} />
  }
  // ステータスはリンクテキストを見ない（表示ラベルは値から引く。wbsStatusLinkHref 参照）。
  const wbsItemStatus = href ? parseWbsItemStatusLinkHref(href) : null
  if (wbsItemStatus) {
    return <WbsItemStatusLinkChip status={wbsItemStatus} />
  }
  // 既定のサニタイズを通らなかった URI は javascript:void(0) に潰されて届く（`wbs:` や
  // `status:` の綴り・値を誤った場合もここに来る）。クリックしても何も起きないリンクを見せる意味はないため、
  // リンクにせず本文の文字として出す。React が javascript: URL に警告を出す件も避けられる。
  if (href?.startsWith('javascript:')) {
    return <>{children}</>
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  )
}

// アシスタント応答をブロック列に組み立てる。ツール呼び出しは吹き出しの外に独立して置くため、
// テキストセグメントごとに吹き出しを分ける。サーバ側の content block の切れ目がそのまま
// 吹き出しの切れ目になる。
//
// ツールのチップは「いま何を操作しているか」を示す進行中の表示なので、末尾のセグメントのときだけ
// 描画する。次のツールへ進めば表示は差し替わり、本文テキストが始まれば消えて吹き出しに切り替わる。
// 階層探索のように同じツールが何十回も呼ばれても 1 行を占めるだけで済む。
// セグメント列自体は保持したまま描画側で間引くことで、index キーが安定し再マウントを避けられる。
const renderAssistantBlocks = (segments: MessageSegment[]) =>
  // セグメントは追記のみで並び替え・途中削除が発生しないため、index キーで安全。
  segments.map((segment, index) => {
    if (segment.kind === 'tool') {
      if (index !== segments.length - 1) {
        return null
      }
      return (
        <ToolCallSegment
          key={`segment-${index}`}
          toolName={segment.toolName}
          toolInput={segment.toolInput}
        />
      )
    }
    return (
      <MessageBubble key={`segment-${index}`} isUser={false}>
        <MarkdownContent>
          {/* remarkBreaks で改行1つを <br> にする。CommonMark の soft break は HTML 上ただの
              改行文字にしかならず、MarkdownContent の whiteSpace: normal に畳まれて消えるため、
              エージェントが改行だけで区切った行（箇条書きにしない列挙など）が繋がってしまう。
              components.a を渡すと linkTarget は効かなくなる（MarkdownLink 側で target を明示）。 */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            transformLinkUri={transformLinkUri}
            components={{ a: MarkdownLink }}
          >
            {segment.text}
          </ReactMarkdown>
        </MarkdownContent>
      </MessageBubble>
    )
  })

// アシスタントの応答のみマークダウンとして解釈する。
// ユーザー入力・エラーはプレーンテキスト（バブルの pre-wrap で改行を保持）のまま。
const renderMessageBlocks = (
  message: AiAgentChatMessage,
  isUser: boolean,
  showLoading: boolean
) => {
  if (showLoading) {
    return (
      <MessageBubble isUser={false}>
        <LoadingBubble />
      </MessageBubble>
    )
  }
  if (isUser || message.isError) {
    return (
      <MessageBubble isUser={isUser} isError={message.isError}>
        {message.images && message.images.length > 0 && (
          <UserImagesRow>
            {message.images.map((image, index) => (
              <UserImageThumb
                key={`${image.url}-${index}`}
                src={image.url}
                alt=""
              />
            ))}
          </UserImagesRow>
        )}
        {message.wbsItemAttachments &&
          message.wbsItemAttachments.length > 0 && (
            <UserWbsItemAttachmentsRow>
              {message.wbsItemAttachments.map(wbsItem => (
                <WbsItemAttachmentChip key={wbsItem.code} wbsItem={wbsItem} />
              ))}
            </UserWbsItemAttachmentsRow>
          )}
        {segmentsToText(message.segments)}
      </MessageBubble>
    )
  }
  return renderAssistantBlocks(message.segments)
}

type AiAgentMessageListProps = {
  messages: AiAgentChatMessage[]
  isStreaming: boolean
  className?: string
}

const AiAgentMessageListComponent = ({
  messages,
  isStreaming,
  className,
}: AiAgentMessageListProps) => {
  const historyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight
    }
  }, [messages])

  return (
    <HistoryArea ref={historyRef} className={className}>
      {messages.map((message, index) => {
        const isUser = message.role === 'user'
        const showLoading =
          message.role === 'assistant' &&
          !message.complete &&
          message.segments.length === 0 &&
          isStreaming
        return (
          // 会話履歴は追記のみで並び替え・途中削除が発生しないため、index キーで安全。
          <MessageContainer key={`message-${index}`} isUser={isUser}>
            {!isUser && (
              <AiAvatar>
                <SmartToyIcon fontSize="small" />
              </AiAvatar>
            )}
            <MessageBlocks isUser={isUser}>
              {renderMessageBlocks(message, isUser, showLoading)}
            </MessageBlocks>
          </MessageContainer>
        )
      })}
    </HistoryArea>
  )
}

// パネルをドラッグしている間、親は毎フレーム再描画される。会話履歴の描画は重いため、
// 内容が変わっていないときは描画をスキップする。
export const AiAgentMessageList = memo(AiAgentMessageListComponent)
